/**
 * App Component with React Router
 * Main application entry point with route configuration
 */

import React from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { UserRole, Company, Transaction, User } from './types';
import { Layout } from './components/Layout';
import { StatementRecorder } from './components/views/StatementRecorder';
import { MutualFundRecorder } from './components/views/MutualFundRecorder';
import { TransactionManager } from './components/views/TransactionManager';
import { AdminLayout } from './components/views/AdminLayout';
import { CompanyManager } from './components/views/CompanyManager';
import { CompanyDetail } from './components/views/CompanyDetail';
import { UserManager } from './components/views/UserManager';
import { Settings } from './components/views/Settings';
import { Login } from './components/views/Login';
import { NotFound } from './components/views/NotFound';
import { useAuth, AuthUser } from './contexts/AuthContext';
import { ROUTES, ProtectedRoute, getDefaultRedirect } from './routes';
import { transactionsApi, companiesApi, accountsApi, usersApi } from './services/api.service';

// Convert AuthUser to legacy User format for backward compatibility
const authUserToUser = (authUser: AuthUser): User => ({
  id: authUser.user_id,
  name: authUser.name,
  email: authUser.email,
  role: authUser.role as UserRole,
  allowedCompanyIds: authUser.allowedCompanyIds || [],
});

// ============================================
// Mock Data (will be replaced with API calls)
// ============================================

const INITIAL_COMPANIES: Company[] = [
  { 
    id: 'c1', 
    name: 'Global Corp Holdings', 
    industry: 'Conglomerate', 
    status: 'active', 
    isHolding: true,
    taxId: 'US-EIN-99-23841',
    incorporationDate: '1998-05-12',
    address: '1200 Finance Way, Suite 500',
    country: 'United States',
    contactEmail: 'legal@globalcorp.com',
    bankAccounts: [
      { id: 'b1', bankName: 'Chase Business', accountNumber: '****4432', accountName: 'Global Corp Holdings LLC', swiftCode: 'CHASUS33', currency: 'USD', status: 'active', accountType: 'BANK' },
      { id: 'b_mandiri', bankName: 'Bank Mandiri', accountNumber: '1111111111111', accountName: 'Global Corp Mandiri', swiftCode: 'BMRIIDJA', currency: 'IDR', status: 'active', accountType: 'BANK' },
      { id: 'b_bca', bankName: 'Bank BCA', accountNumber: '222222222', accountName: 'Global Corp BCA', swiftCode: 'CENAIDJA', currency: 'IDR', status: 'active', accountType: 'BANK' },
      { id: 'mf_schroders', bankName: 'Schroders', accountNumber: 'RD-SCH-99', accountName: 'Schroders Dana Prestasi', currency: 'IDR', status: 'active', accountType: 'INVESTMENT' },
    ]
  },
  { 
    id: 'c2', 
    name: 'Global Tech Systems', 
    industry: 'Technology', 
    status: 'active', 
    parentId: 'c1',
    country: 'United States',
    bankAccounts: [
      { id: 'b2', bankName: 'Silicon Valley Bank', accountNumber: '****9988', accountName: 'Global Tech Systems Inc.', swiftCode: 'SVBUS66', currency: 'USD', status: 'active', accountType: 'BANK' },
    ]
  },
  { 
    id: 'c3', 
    name: 'Global Logistics', 
    industry: 'Transport', 
    status: 'active', 
    parentId: 'c1',
    country: 'Singapore',
    bankAccounts: [
      { id: 'b4_idr', bankName: 'BRI', accountNumber: '0341-01-****10-50-2', accountName: 'PT Global Logistics Indo', swiftCode: 'BRINIDJA', currency: 'IDR', status: 'active', accountType: 'BANK' },
    ]
  },
];

const INITIAL_USERS: User[] = [
  { id: 'u1', name: 'System Administrator', email: 'admin@wbbgroup.com', role: UserRole.ADMINISTRATOR, allowedCompanyIds: [] },
  { id: 'u2', name: 'Staf Admin Demo', email: 'stafadmin@wbbgroup.com', role: UserRole.STAF_ADMIN, allowedCompanyIds: [] },
  { id: 'u3', name: 'Staf HO Demo', email: 'stafho@wbbgroup.com', role: UserRole.STAF_HO, allowedCompanyIds: ['c1', 'c2'] },
  { id: 'u4', name: 'Staf Finance Demo', email: 'staffinance@wbbgroup.com', role: UserRole.STAF_FINANCE, allowedCompanyIds: ['c1', 'c3'] },
];

// ============================================
// Layout Wrapper Component
// ============================================

const AppLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  if (!user) return null;
  
  const currentUser = authUserToUser(user);

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  return (
    <Layout 
      currentUser={currentUser}
      onLogout={handleLogout}
    />
  );
};

// ============================================
// Main App Component
// ============================================

export const App: React.FC = () => {
  const { isLoading, isAuthenticated, user } = useAuth();
  
  // State for data from API
  const [companies, setCompanies] = React.useState<Company[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = React.useState<string>('');
  const [isLoadingData, setIsLoadingData] = React.useState(true);

  // Fetch companies from API when authenticated
  React.useEffect(() => {
    const fetchCompanies = async () => {
      if (!isAuthenticated) return;
      
      try {
        setIsLoadingData(true);
        const response = await companiesApi.getAll();
        
        // Response is PaginatedResponse with data array
        const companiesData = response?.data || response || [];
        
        // Transform API response to match frontend Company type
        const transformedCompanies: Company[] = (Array.isArray(companiesData) ? companiesData : []).map((c: any) => ({
          id: c.id || c.company_id,
          name: c.name,
          industry: c.industry || '',
          status: (c.status?.toLowerCase() || 'active') as 'active' | 'inactive',
          isHolding: c.isHolding || false,
          parentId: c.parent_id || c.parentId || undefined,
          logoUrl: c.logoUrl || undefined,
          taxId: c.taxId || '',
          address: c.address || '',
          country: c.country || '',
          contactEmail: c.contactEmail || '',
          spreadsheetId: '', // Deprecated - use statementSpreadsheetId and mutualFundSpreadsheetId
          statementSpreadsheetId: c.statementSpreadsheetId || '',
          mutualFundSpreadsheetId: c.mutualFundSpreadsheetId || '',
          bankAccounts: (c.bankAccounts || [])
            .filter((acc: any) => acc && acc.account_id)
            .map((acc: any) => ({
              id: acc.id || acc.account_id,
              bankName: acc.bankName,
              accountNumber: acc.accountNumber,
              accountName: acc.accountName,
              currency: acc.currency || 'IDR',
              balance: acc.balance || 0,
              status: (acc.status?.toLowerCase() || 'active') as 'active' | 'inactive',
            })),
        }));
        
        setCompanies(transformedCompanies);
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        // Fallback to mock data if API fails
        setCompanies(INITIAL_COMPANIES);
      } finally {
        setIsLoadingData(false);
      }
    };
    
    fetchCompanies();
  }, [isAuthenticated]);

  // Fetch users from API when authenticated (only for roles with permission)
  React.useEffect(() => {
    const fetchUsers = async () => {
      if (!isAuthenticated || !user) return;
      
      // Only admins and staff admins can view users
      const allowedRoles = ['ADMINISTRATOR', 'STAF_ADMIN', 'STAF_HO'];
      if (!allowedRoles.includes(user.role)) {
        return;
      }
      
      try {
        const response = await usersApi.getAll();
        
        // Response is PaginatedResponse with data array
        const usersData = response?.data || response || [];
        
        // Transform API response to match frontend User type
        const transformedUsers: User[] = (Array.isArray(usersData) ? usersData : []).map((u: any) => ({
          id: u.user_id,
          name: u.name,
          email: u.email,
          role: (u.role?.toUpperCase() || 'STAF_ADMIN') as UserRole,
          allowedCompanyIds: u.allowedCompanyIds ? (typeof u.allowedCompanyIds === 'string' ? JSON.parse(u.allowedCompanyIds) : u.allowedCompanyIds) : [],
        }));
        
        setUsers(transformedUsers);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        // Fallback to mock data if API fails
        setUsers(INITIAL_USERS);
      }
    };
    
    fetchUsers();
  }, [isAuthenticated, user]);

  // Get current user in legacy format
  const currentUser = user ? authUserToUser(user) : null;

  // Prevent double submission
  const [isSavingTransaction, setIsSavingTransaction] = React.useState(false);

  // Transaction handler - save to database
  const handleAddTransaction = async (newTx: Transaction, source: 'STATEMENT' | 'MUTUAL_FUND' = 'STATEMENT') => {
    // Prevent double submission
    if (isSavingTransaction) {
      console.log('Transaction save already in progress, skipping...');
      return;
    }
    
    setIsSavingTransaction(true);
    try {
      const result = await transactionsApi.create({
        companyId: newTx.companyId,
        bankAccountId: newTx.bankAccountId || undefined,
        date: newTx.date,
        description: newTx.description,
        amount: newTx.amount,
        units: newTx.units,
        type: newTx.type.toUpperCase() as 'CREDIT' | 'DEBIT',
        source,
        imageUrl: newTx.imageUrl,
      });
      console.log('Transaction saved:', result);
    } catch (error) {
      console.error('Failed to save transaction:', error);
      alert('Gagal menyimpan transaksi. Silakan coba lagi.');
    } finally {
      setIsSavingTransaction(false);
    }
  };

  // Source-specific transaction handlers
  const handleAddStatementTransaction = (newTx: Transaction) => handleAddTransaction(newTx, 'STATEMENT');
  const handleAddMutualFundTransaction = (newTx: Transaction) => handleAddTransaction(newTx, 'MUTUAL_FUND');

  // Company CRUD - connected to database
  const handleAddCompany = async (newCompany: Company) => {
    try {
      const result = await companiesApi.create({
        name: newCompany.name,
        industry: newCompany.industry,
        status: newCompany.status?.toUpperCase() as 'ACTIVE' | 'INACTIVE',
        isHolding: newCompany.isHolding,
        parent_id: newCompany.parentId,
      });
      
      // Add with ID from API
      const createdCompany: Company = {
        ...newCompany,
        id: (result as any).id || (result as any).company_id,
      };
      setCompanies([...companies, createdCompany]);
    } catch (error) {
      console.error('Failed to create company:', error);
      alert('Gagal membuat perusahaan. Silakan coba lagi.');
    }
  };

  const handleUpdateCompany = async (updatedCompany: Company) => {
    try {
      await companiesApi.update(updatedCompany.id, {
        name: updatedCompany.name,
        industry: updatedCompany.industry,
        status: updatedCompany.status?.toUpperCase() as 'ACTIVE' | 'INACTIVE',
        isHolding: updatedCompany.isHolding,
        parent_id: updatedCompany.parentId,
        bankAccounts: updatedCompany.bankAccounts || [],
        logoUrl: updatedCompany.logoUrl,
        taxId: updatedCompany.taxId,
        address: updatedCompany.address,
        country: updatedCompany.country,
        contactEmail: updatedCompany.contactEmail,
        spreadsheetId: updatedCompany.spreadsheetId,
        statementSpreadsheetId: updatedCompany.statementSpreadsheetId,
        mutualFundSpreadsheetId: updatedCompany.mutualFundSpreadsheetId,
      });
      setCompanies(companies.map(c => c.id === updatedCompany.id ? updatedCompany : c));
    } catch (error) {
      console.error('Failed to update company:', error);
      alert('Gagal mengupdate perusahaan. Silakan coba lagi.');
    }
  };

  const handleDeleteCompany = async (id: string) => {
    try {
      await companiesApi.delete(id);
      setCompanies(companies.filter(c => c.id !== id));
    } catch (error) {
      console.error('Failed to delete company:', error);
      alert('Gagal menghapus perusahaan. Silakan coba lagi.');
    }
  };

  // User CRUD - connected to database
  const handleCreateUser = async (newUser: User & { password: string }) => {
    try {
      const result = await usersApi.create({
        email: newUser.email,
        name: newUser.name,
        role: newUser.role.toUpperCase() as 'ADMINISTRATOR' | 'STAF_ADMIN' | 'STAF_HO' | 'STAF_FINANCE',
        allowedCompanyIds: newUser.allowedCompanyIds,
        password: newUser.password,
      });
      
      // Transform API response to local User format
      const createdUser: User = {
        id: result.user_id,
        name: result.name,
        email: result.email,
        role: result.role.toLowerCase() as UserRole,
        allowedCompanyIds: result.allowedCompanyIds 
          ? (typeof result.allowedCompanyIds === 'string' 
            ? JSON.parse(result.allowedCompanyIds) 
            : result.allowedCompanyIds) 
          : [],
      };
      
      setUsers([...users, createdUser]);
      
      // Show success message with credentials
      alert(`User berhasil dibuat!\n\nEmail: ${result.email}\nPassword: ${newUser.password}\n\nSimpan informasi ini untuk diberikan kepada pengguna.`);
      console.log('User created:', result);
    } catch (error) {
      console.error('Failed to create user:', error);
      alert('Gagal menambahkan pengguna. Silakan coba lagi.');
    }
  };

  const handleUpdateUser = async (updatedUser: User) => {
    try {
      await usersApi.update(updatedUser.id, {
        name: updatedUser.name,
        email: updatedUser.email,
        role: updatedUser.role.toUpperCase() as 'ADMINISTRATOR' | 'STAF_ADMIN' | 'STAF_HO' | 'STAF_FINANCE',
        allowedCompanyIds: updatedUser.allowedCompanyIds,
      });
      
      setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
      console.log('User updated:', updatedUser);
    } catch (error) {
      console.error('Failed to update user:', error);
      alert('Gagal memperbarui pengguna. Silakan coba lagi.');
    }
  };

  const handleRemoveUser = async (id: string) => {
    try {
      await usersApi.delete(id);
      setUsers(users.filter(u => u.id !== id));
      console.log('User deleted:', id);
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Gagal menghapus pengguna. Silakan coba lagi.');
    }
  };

  // Get accessible companies for current user
  const accessibleCompanies = React.useMemo(() => {
    if (!currentUser) return [];
    if (currentUser.role === UserRole.ADMINISTRATOR || currentUser.role === UserRole.STAF_ADMIN) {
      return companies;
    }
    const allowed = currentUser.allowedCompanyIds || [];
    return companies.filter(c => allowed.includes(c.id));
  }, [currentUser, companies]);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#0F172A]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path={ROUTES.LOGIN} 
        element={
          isAuthenticated 
            ? <Navigate to={ROUTES.STATEMENT_RECORDER} replace /> 
            : <Login />
        } 
      />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          {/* Default redirect */}
          <Route index element={<Navigate to={ROUTES.STATEMENT_RECORDER} replace />} />
          
          {/* Statement Recorder */}
          <Route 
            path={ROUTES.STATEMENT_RECORDER} 
            element={
              <StatementRecorder 
                companies={accessibleCompanies}
                selectedCompanyId={selectedCompanyId}
                onSelectCompany={setSelectedCompanyId}
                onSave={handleAddStatementTransaction}
              />
            } 
          />
          
          {/* Mutual Fund Recorder */}
          <Route 
            path={ROUTES.MUTUAL_FUND_RECORDER} 
            element={
              <MutualFundRecorder
                companies={accessibleCompanies}
                selectedCompanyId={selectedCompanyId}
                onSelectCompany={setSelectedCompanyId}
                onSave={handleAddMutualFundTransaction}
              />
            } 
          />

          {/* Transaction Manager - Admin only */}
          <Route 
            path={ROUTES.TRANSACTIONS} 
            element={<TransactionManager />} 
          />

          {/* User Settings - accessible by all authenticated users */}
          <Route 
            path={ROUTES.USER_SETTINGS} 
            element={
              currentUser ? (
                <Settings currentUser={currentUser} />
              ) : null
            } 
          />
          
          {/* Settings Section with nested routes */}
          <Route path={ROUTES.SETTINGS} element={<AdminLayout />}>
            {/* Redirect /setting to /setting/companies */}
            <Route 
              index 
              element={<Navigate to={ROUTES.SETTINGS_COMPANIES} replace />}
            />
            
            {/* Company Manager */}
            <Route 
              path="companies" 
              element={
                currentUser ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-xl text-sm">
                      <strong>Manajemen Perusahaan:</strong> Di sini Anda dapat menambah Perusahaan, Grup Holding, serta menambahkan Rekening Bank dan Reksadana ke masing-masing entitas.
                    </div>
                    <CompanyManager
                      companies={companies}
                      users={users}
                      userRole={currentUser.role}
                      onAddCompany={handleAddCompany}
                      onUpdateCompany={handleUpdateCompany}
                      onDeleteCompany={handleDeleteCompany}
                    />
                  </div>
                ) : null
              } 
            />

            {/* Company Detail with optional tab */}
            <Route 
              path="companies/:companyName/:tab?" 
              element={
                currentUser ? (
                  <CompanyDetail
                    companies={companies}
                    users={users}
                    userRole={currentUser.role}
                    onUpdateCompany={handleUpdateCompany}
                    onDeleteCompany={handleDeleteCompany}
                  />
                ) : null
              } 
            />
            
            {/* User Manager */}
            <Route 
              path="users" 
              element={
                currentUser ? (
                  <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 text-purple-800 px-4 py-3 rounded-xl text-sm">
                      <strong>Manajemen Pengguna (RBAC):</strong> Tambahkan pengguna baru, tentukan peran (HO/Finance), dan batasi akses mereka ke perusahaan tertentu.
                    </div>
                    <UserManager
                      users={users}
                      currentUser={currentUser}
                      companies={companies}
                      onCreateUser={handleCreateUser}
                      onUpdateUser={handleUpdateUser}
                      onRemoveUser={handleRemoveUser}
                    />
                  </div>
                ) : null
              } 
            />
          </Route>
        </Route>
      </Route>

      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
